// src/components/tenant/TenantDetailsModal.tsx
// ✅ UPDATED: Cognito-native tenant details modal

import React, { useState, useEffect } from 'react';
import { 
  X, 
  Building2, 
  Edit, 
  Save, 
  Mail, 
  Phone, 
  MapPin, 
  Users, 
  Package, 
  HardDrive,
  Crown,
  Calendar,
  Power,
  AlertCircle,
  CheckCircle,
  Clock,
  PlayCircle,
  Pause,
  XCircle,
  Shield,
  UserPlus
} from 'lucide-react';
import { tenantManagementAPI } from '../../services/tenantManagementAPI';
import { userProvisioningAPI, TenantUser } from '../../services/userProvisioningAPI';
import { 
  TenantRecord, 
  UpdateTenantRequest, 
  UpdateTenantStatusRequest,
  TenantStatus,
  SubscriptionTier,
  OrganizationType 
} from '../../types/tenant';
import AdminUserCard from './AdminUserCard';
import InviteAdminModal from './InviteAdminModal';

interface TenantDetailsModalProps {
  isOpen: boolean;
  tenant: TenantRecord;
  onClose: () => void;
  onTenantUpdated: (tenant: TenantRecord) => void;
}

const TenantDetailsModal: React.FC<TenantDetailsModalProps> = ({
  isOpen,
  tenant,
  onClose,
  onTenantUpdated
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<UpdateTenantRequest>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusUpdateLoading, setStatusUpdateLoading] = useState(false);
  
  // Admin user state - Updated for Cognito
  const [adminUser, setAdminUser] = useState<TenantUser | undefined>(undefined);
  const [adminLoading, setAdminLoading] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Load admin user data when modal opens
  useEffect(() => {
    if (isOpen && tenant) {
      loadAdminData();
    }
  }, [isOpen, tenant.tenantId]);

  const loadAdminData = async () => {
    setAdminLoading(true);
    try {
      console.log(`🔍 Loading admin data for tenant: ${tenant.tenantId}`);
      const response = await userProvisioningAPI.getTenantUsers(tenant.tenantId);
      
      if (response.success && response.data) {
        // Find the OrgAdmin user (should only be one for initial setup)
        const orgAdmin = response.data.users.find(u => u.role === 'OrgAdmin');
        setAdminUser(orgAdmin);
        console.log('✅ Admin data loaded:', orgAdmin);
      } else {
        console.log('ℹ️ No admin data found or error:', response.error);
        setAdminUser(undefined);
      }
    } catch (err: any) {
      console.error('❌ Error loading admin data:', err);
      setAdminUser(undefined);
    } finally {
      setAdminLoading(false);
    }
  };

  if (!isOpen) return null;

  const getStatusIcon = (status: TenantStatus) => {
    switch (status) {
      case 'ACTIVE': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'TRIAL': return <Crown className="w-4 h-4 text-yellow-500" />;
      case 'INACTIVE': return <Power className="w-4 h-4 text-gray-500" />;
      case 'SUSPENDED': return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'PENDING_SETUP': return <Clock className="w-4 h-4 text-blue-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: TenantStatus) => {
    switch (status) {
      case 'ACTIVE': return 'bg-green-100 text-green-800 border-green-200';
      case 'TRIAL': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'INACTIVE': return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'SUSPENDED': return 'bg-red-100 text-red-800 border-red-200';
      case 'PENDING_SETUP': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const organizationTypes: { value: OrganizationType; label: string }[] = [
    { value: 'POLICE_DEPARTMENT', label: 'Police Department' },
    { value: 'SHERIFF_OFFICE', label: 'Sheriff Office' },
    { value: 'STATE_POLICE', label: 'State Police' },
    { value: 'FEDERAL_AGENCY', label: 'Federal Agency' },
    { value: 'OTHER', label: 'Other' }
  ];

  const subscriptionTiers: { value: SubscriptionTier; label: string }[] = [
    { value: 'BASIC', label: 'Basic' },
    { value: 'PREMIUM', label: 'Premium' },
    { value: 'ENTERPRISE', label: 'Enterprise' }
  ];

  // Status change handler
  const handleStatusChange = async (newStatus: TenantStatus, reason?: string) => {
    if (!tenant) return;
    
    setStatusUpdateLoading(true);
    setError(null);
    
    try {
      const statusRequest: UpdateTenantStatusRequest = {
        status: newStatus,
        reason: reason || `Status changed to ${newStatus}`
      };
      
      console.log(`🔄 Updating tenant ${tenant.tenantId} status to ${newStatus}`);
      
      const response = await tenantManagementAPI.updateTenantStatus(tenant.tenantId, statusRequest);
      
      if (response.success && response.data) {
        console.log('✅ Tenant status updated successfully');
        const updatedTenant = response.data.tenant || response.data;
        onTenantUpdated(updatedTenant);
      } else {
        throw new Error(response.error?.message || 'Failed to update tenant status');
      }
    } catch (err: any) {
      console.error('❌ Error updating tenant status:', err);
      setError(err.message || 'Failed to update tenant status');
    } finally {
      setStatusUpdateLoading(false);
    }
  };

  const handleInviteAdmin = () => {
    setShowInviteModal(true);
  };

  const handleInvitationSent = () => {
    loadAdminData();
    setShowInviteModal(false);
  };

 // Handle feature toggle
  const handleFeatureToggle = async (featureName: string) => {
    if (!tenant) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const currentFeatures = tenant.features || [];
      const hasFeature = currentFeatures.includes(featureName);
      
      // Toggle the feature
      const updatedFeatures = hasFeature
        ? currentFeatures.filter(f => f !== featureName)
        : [...currentFeatures, featureName];
      
      console.log(`🔄 Toggling feature ${featureName} for tenant ${tenant.tenantId}: ${hasFeature ? 'OFF' : 'ON'}`);
      
      const response = await tenantManagementAPI.updateTenant(tenant.tenantId, {
        features: updatedFeatures
      });
      
      if (response.success && response.data) {
        console.log('✅ Feature toggle successful');
        const updatedTenant = response.data.tenant || response.data;
        onTenantUpdated(updatedTenant);
      } else {
        throw new Error(response.error?.message || 'Failed to update features');
      }
    } catch (err: any) {
      console.error('❌ Error toggling feature:', err);
      setError(err.message || 'Failed to update features');
    } finally {
      setLoading(false);
    }
  };

  // Get available status actions based on current status
  const getStatusActions = () => {
    const actions = [];
    
    switch (tenant.status) {
      case 'PENDING_SETUP':
        actions.push({
          status: 'ACTIVE' as TenantStatus,
          label: 'Activate Tenant',
          icon: PlayCircle,
          color: 'bg-green-600 hover:bg-green-700 text-white',
          reason: 'Tenant activated and ready for use'
        });
        actions.push({
          status: 'TRIAL' as TenantStatus,
          label: 'Start Trial',
          icon: Crown,
          color: 'bg-yellow-600 hover:bg-yellow-700 text-white',
          reason: 'Tenant started on trial period'
        });
        break;
        
      case 'ACTIVE':
        actions.push({
          status: 'SUSPENDED' as TenantStatus,
          label: 'Suspend',
          icon: Pause,
          color: 'bg-red-600 hover:bg-red-700 text-white',
          reason: 'Tenant suspended by admin'
        });
        actions.push({
          status: 'INACTIVE' as TenantStatus,
          label: 'Deactivate',
          icon: XCircle,
          color: 'bg-gray-600 hover:bg-gray-700 text-white',
          reason: 'Tenant deactivated'
        });
        break;
        
      case 'TRIAL':
        actions.push({
          status: 'ACTIVE' as TenantStatus,
          label: 'Activate',
          icon: PlayCircle,
          color: 'bg-green-600 hover:bg-green-700 text-white',
          reason: 'Trial converted to active subscription'
        });
        actions.push({
          status: 'SUSPENDED' as TenantStatus,
          label: 'Suspend',
          icon: Pause,
          color: 'bg-red-600 hover:bg-red-700 text-white',
          reason: 'Tenant suspended by admin'
        });
        break;
        
      case 'SUSPENDED':
        actions.push({
          status: 'ACTIVE' as TenantStatus,
          label: 'Reactivate',
          icon: PlayCircle,
          color: 'bg-green-600 hover:bg-green-700 text-white',
          reason: 'Tenant reactivated by admin'
        });
        break;
        
      case 'INACTIVE':
        actions.push({
          status: 'ACTIVE' as TenantStatus,
          label: 'Reactivate',
          icon: PlayCircle,
          color: 'bg-green-600 hover:bg-green-700 text-white',
          reason: 'Tenant reactivated by admin'
        });
        break;
    }
    
    return actions;
  };

  const statusActions = getStatusActions();

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
          
          <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
            {/* Header */}
            <div className="bg-white px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Building2 className="w-6 h-6 mr-3 text-blue-600" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {tenant.organizationName}
                    </h3>
                    <div className="flex items-center space-x-3 text-sm text-gray-500 mt-1">
                      <span>Tenant ID: {tenant.tenantId}</span>
                      {tenant.tenantSlug && (
                        <>
                          <span>•</span>
                          <span className="inline-flex items-center">
                            <span className="font-medium text-blue-600">Department Key:</span>
                            <code className="ml-1.5 px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-mono border border-blue-200">
                              {tenant.tenantSlug}
                            </code>
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <span className={`inline-flex items-center px-3 py-1 rounded-md text-sm font-medium border ${getStatusColor(tenant.status)}`}>
                    {getStatusIcon(tenant.status)}
                    <span className="ml-2">{tenant.status}</span>
                  </span>
                  
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            {/* Status Actions Bar */}
            {statusActions.length > 0 && (
              <div className="bg-gray-50 px-6 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Shield className="w-4 h-4 text-gray-500 mr-2" />
                    <span className="text-sm font-medium text-gray-700">Status Actions:</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    {statusActions.map((action) => {
                      const IconComponent = action.icon;
                      return (
                        <button
                          key={action.status}
                          onClick={() => handleStatusChange(action.status, action.reason)}
                          disabled={statusUpdateLoading}
                          className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${action.color}`}
                        >
                          <IconComponent className="w-3 h-3 mr-1.5" />
                          {statusUpdateLoading ? 'Updating...' : action.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Content */}
            <div className="bg-white px-6 py-4">
              {error && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <AlertCircle className="w-5 h-5 text-red-400 mr-3" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* Admin User Section */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-900">Administrator User</h4>
                    {!adminLoading && !adminUser && (
                      <button
                        onClick={handleInviteAdmin}
                        className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <UserPlus className="w-3 h-3 mr-1.5" />
                        Invite Admin
                      </button>
                    )}
                  </div>
                  
                  {adminLoading ? (
                    <div className="bg-gray-50 rounded-lg p-4 animate-pulse">
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gray-300 rounded-full"></div>
                        <div className="flex-1 space-y-2">
                          <div className="h-4 bg-gray-300 rounded w-1/2"></div>
                          <div className="h-3 bg-gray-300 rounded w-3/4"></div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <AdminUserCard
                      tenantId={tenant.tenantId}
                      adminUser={adminUser}
                      onUpdate={loadAdminData}
                      onInviteNew={handleInviteAdmin}
                    />
                  )}
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Organization Details</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Organization Name</label>
                        <p className="text-sm text-gray-900">{tenant.organizationName}</p>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
                        <p className="text-sm text-gray-900">
                          {organizationTypes.find(t => t.value === tenant.organizationType)?.label || tenant.organizationType}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Contact Information</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
                        <div className="flex items-center">
                          <Mail className="w-4 h-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-900">{tenant.contactEmail}</p>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Phone</label>
                        <div className="flex items-center">
                          <Phone className="w-4 h-4 text-gray-400 mr-2" />
                          <p className="text-sm text-gray-900">{tenant.contactPhone || 'Not provided'}</p>
                        </div>
                      </div>
                    </div>

                    {tenant.address && (
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Address</label>
                        <div className="flex items-start">
                          <MapPin className="w-4 h-4 text-gray-400 mr-2 mt-0.5" />
                          <div className="text-sm text-gray-900">
                            <p>{tenant.address}</p>
                            {(tenant.city || tenant.state) && (
                              <p>{tenant.city}{tenant.city && tenant.state && ', '}{tenant.state} {tenant.zipCode}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">System Limits</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="text-center">
                        <Users className="w-6 h-6 text-blue-600 mx-auto mb-2" />
                        <p className="text-lg font-semibold text-gray-900">{tenant.maxUsers.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">Max Users</p>
                      </div>
                      <div className="text-center">
                        <Package className="w-6 h-6 text-green-600 mx-auto mb-2" />
                        <p className="text-lg font-semibold text-gray-900">{tenant.maxInventoryItems.toLocaleString()}</p>
                        <p className="text-xs text-gray-500">Max Items</p>
                      </div>
                      <div className="text-center">
                        <HardDrive className="w-6 h-6 text-purple-600 mx-auto mb-2" />
                        <p className="text-lg font-semibold text-gray-900">{tenant.maxStorageGB} GB</p>
                        <p className="text-xs text-gray-500">Storage</p>
                      </div>
                    </div>
                  </div>
               </div>

                {/* Enabled Modules Section */}
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Enabled Modules</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Package className="w-5 h-5 text-purple-600 mr-3" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">Asset Tracking</p>
                          <p className="text-xs text-gray-500">Track high-value "small and attractive" items with annual verification</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleFeatureToggle('ASSET_TRACKING')}
                        disabled={loading}
                        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                          tenant.features?.includes('ASSET_TRACKING') 
                            ? 'bg-blue-600' 
                            : 'bg-gray-200'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            tenant.features?.includes('ASSET_TRACKING') 
                              ? 'translate-x-5' 
                              : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Subscription</h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <Crown className="w-5 h-5 text-yellow-600 mr-2" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {subscriptionTiers.find(t => t.value === tenant.subscriptionTier)?.label || tenant.subscriptionTier}
                          </p>
                          <p className="text-xs text-gray-500">Subscription Tier</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">Contact for Pricing</p>
                        <p className="text-xs text-gray-500">Current Rate</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Metadata</h4>
                  <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Created</label>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-900">
                            <p>{formatDate(tenant.createdAt)}</p>
                            <p className="text-xs text-gray-500">by {tenant.createdBy}</p>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-500 mb-1">Updated</label>
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                          <div className="text-sm text-gray-900">
                            <p>{formatDate(tenant.updatedAt)}</p>
                            <p className="text-xs text-gray-500">by {tenant.updatedBy}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Admin Modal */}
      <InviteAdminModal
        isOpen={showInviteModal}
        tenant={tenant}
        onClose={() => setShowInviteModal(false)}
        onInvitationSent={handleInvitationSent}
      />
    </>
  );
};

export default TenantDetailsModal;
